import React, { useState, useEffect } from 'react';

const TYPE_LABELS = {
  agent_initial:        { label: 'Initial Outreach',     color: '#14504A' },
  agent_followup_3:     { label: 'Day 3 Bump',           color: '#E0A526' },
  agent_followup_7:     { label: 'Day 7 Final',          color: '#E0A526' },
  agent_reply:          { label: 'Agent Reply',          color: '#6B6760' },
  agent_counter:        { label: 'Counter Offer',        color: '#14504A' },
  buyer_announcement:   { label: 'Buyer Blast',          color: '#14504A' },
  buyer_followup_2:     { label: 'Buyer Day 2',          color: '#E0A526' },
  buyer_followup_3:     { label: 'Buyer Day 3 SMS',      color: '#E0A526' },
};

function relativeTime(iso) {
  if (!iso) return '';
  const dt = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  const diff = Date.now() - dt.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const dy = Math.round(hr / 24);
  return `${dy}d ago`;
}

export default function CommunicationsTimeline({ dealId, refreshKey = 0 }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats]     = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!dealId) return;
    setLoading(true);
    Promise.all([
      fetch(`/api/communications/${dealId}`).then(r => r.json()),
      fetch(`/api/communications/stats/${dealId}`).then(r => r.json()),
    ]).then(([list, s]) => {
      setItems(Array.isArray(list) ? list : []);
      setStats(s);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [dealId, refreshKey]);

  if (loading) return <div style={{ padding: 20, color: 'var(--gray-500)', fontSize: 13 }}>Loading communications…</div>;
  if (!items.length) return <div style={{ padding: 20, color: 'var(--gray-500)', fontSize: 13, textAlign: 'center' }}>No communications yet — send an outreach email to start the thread.</div>;

  return (
    <div>
      {stats && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <StatBox label="Total Sent"    value={stats.sent_count} />
          <StatBox label="Opened"        value={stats.opened_count} color="#6B6760" />
          <StatBox label="Replies"       value={stats.replied_count} color={stats.replied_count > 0 ? '#4A7C59' : 'var(--gray-300)'} />
          <StatBox label="Buyer Reach"   value={stats.buyer_sent} color="#14504A" />
        </div>
      )}

      {stats?.buyer_replies && stats.buyer_replies.length > 0 && (
        <div style={{ background: 'rgba(74, 124, 89,0.06)', border: '1px solid rgba(74, 124, 89,0.25)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#4A7C59', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Interested Buyers ({stats.buyer_replies.length})</div>
          {stats.buyer_replies.map(b => (
            <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(74, 124, 89,0.15)', fontSize: 12 }}>
              <div>
                <div style={{ color: 'var(--white)', fontWeight: 600 }}>{b.name}{b.company && <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}> · {b.company}</span>}</div>
                <div style={{ color: 'var(--gray-500)', fontSize: 11 }}>
                  {[b.email, b.phone].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{relativeTime(b.last_at)}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ position: 'relative', paddingLeft: 22 }}>
        <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: 'var(--navy-border)' }} />
        {items.map(item => {
          const typeMeta = TYPE_LABELS[item.type] || { label: item.type || 'Message', color: 'var(--gray-500)' };
          const isInbound = item.direction === 'inbound';
          const isOpen = expanded[item.id];
          return (
            <div key={item.id} style={{ position: 'relative', marginBottom: 14 }}>
              <div style={{ position: 'absolute', left: -22, top: 6, width: 14, height: 14, borderRadius: '50%', background: isInbound ? '#6B6760' : typeMeta.color, border: '3px solid var(--navy-card)', zIndex: 1 }} />
              <div onClick={() => setExpanded(e => ({ ...e, [item.id]: !isOpen }))} style={{ background: isInbound ? 'rgba(107, 103, 96,0.06)' : 'var(--navy-card)', border: `1px solid ${isInbound ? 'rgba(107, 103, 96,0.25)' : 'var(--navy-border)'}`, borderRadius: 8, padding: '10px 12px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: `${typeMeta.color}15`, color: typeMeta.color, fontWeight: 700, textTransform: 'uppercase' }}>{typeMeta.label}</span>
                    <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(156, 150, 144,0.1)', color: 'var(--gray-500)', fontWeight: 600 }}>{item.channel.toUpperCase()}</span>
                    {item.status === 'simulated' && <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(224, 165, 38,0.1)', color: 'var(--gold)', fontWeight: 600 }}>SIMULATED</span>}
                    {item.status === 'failed' && <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(184, 84, 80,0.1)', color: '#B85450', fontWeight: 600 }}>FAILED</span>}
                    {item.opened_at && <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(107, 103, 96,0.1)', color: '#6B6760', fontWeight: 600 }}>OPENED</span>}
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>{relativeTime(item.sent_at)}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 2 }}>
                  {isInbound ? 'From' : 'To'}: {item.recipient || '—'} {item.buyer_name && <span>· {item.buyer_name}</span>}
                </div>
                {item.subject && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-300)' }}>{item.subject}</div>}
                {isOpen && item.body && (
                  <div style={{ marginTop: 8, padding: 10, background: 'var(--navy)', borderRadius: 6, fontSize: 11, color: 'var(--gray-300)', lineHeight: 1.55, whiteSpace: 'pre-wrap', maxHeight: 280, overflowY: 'auto' }}>{item.body}</div>
                )}
                {!isOpen && item.body && (
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.body.slice(0, 120)}{item.body.length > 120 ? '…' : ''}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatBox({ label, value, color = 'var(--white)' }) {
  return (
    <div style={{ flex: 1, background: 'var(--navy)', borderRadius: 8, padding: '10px 12px', border: '1px solid var(--navy-border)' }}>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value || 0}</div>
      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{label}</div>
    </div>
  );
}
